import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Modal } from '../../components/ui/Modal';
import { RiskBadge } from '../../components/ui/StatusBadge';
import { useAdminLayoutContext } from '../../components/admin/AdminLayout';
import {
  Plus,
  Edit2,
  Trash2,
  Brain,
  AlertTriangle,
  CheckCircle,
  Clock,
  Info,
  ArrowLeft,
  Check,
  GripVertical,
  ArrowDownAZ,
  ListOrdered,
} from 'lucide-react';

type ContentStatus = 'draft' | 'pending' | 'published';
type SortMode = 'alphabetical' | 'manual';

interface SymptomGroup {
  id: string;
  name_no: string;
  name_en?: string;
  description?: string;
  order_index: number;
  status: ContentStatus;
}

interface Symptom {
  id: string;
  group_id: string;
  name_no: string;
  name_en?: string;
  help_text?: string;
  risk_tag?: string;
  order_index?: number;
  status: ContentStatus;
  version?: number;
}

export function GroupSymptomsPage() {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const { profile, hasRole } = useAuth();
  const { setHeaderAction, setPageTitle, setPageSubtitle, setBackTo } = useAdminLayoutContext();

  const [showModal, setShowModal] = useState(false);
  const [editingSymptom, setEditingSymptom] = useState<Symptom | null>(null);
  const [filterStatus, setFilterStatus] = useState<ContentStatus | 'all'>('all');
  const [sortMode, setSortMode] = useState<SortMode>('alphabetical');
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [group, setGroup] = useState<SymptomGroup | null | undefined>(undefined);
  const [symptoms, setSymptoms] = useState<Symptom[] | undefined>(undefined);
  const [formData, setFormData] = useState({
    name_no: '',
    help_text: '',
    risk_tag: '',
    status: 'draft' as ContentStatus,
  });

  const canEdit = hasRole(['superadmin', 'org_admin', 'editor']);

  const riskOptions = [
    { value: '', label: 'Ingen risiko' },
    { value: 'suicide', label: 'Selvmordsrisiko' },
    { value: 'violence', label: 'Voldsrisiko' },
    { value: 'self-harm', label: 'Selvskading' },
    { value: 'psychosis', label: 'Psykose' },
    { value: 'substance', label: 'Rusmisbruk' },
  ];

  // Fetch group details
  const fetchGroup = useCallback(async () => {
    if (!groupId) return;

    const { data, error } = await supabase
      .from('symptom_groups')
      .select('*')
      .eq('id', groupId)
      .single();

    if (error) {
      console.error('Error fetching group:', error);
      setGroup(null);
      return;
    }
    setGroup(data);
  }, [groupId]);

  // Fetch symptoms for this group
  const fetchSymptoms = useCallback(async () => {
    if (!groupId) return;

    const { data, error } = await supabase
      .from('symptoms')
      .select('*')
      .eq('group_id', groupId)
      .order('order_index', { ascending: true });

    if (error) {
      console.error('Error fetching symptoms:', error);
      return;
    }
    setSymptoms(data || []);
  }, [groupId]);

  useEffect(() => {
    fetchGroup();
  }, [fetchGroup]);

  useEffect(() => {
    fetchSymptoms();
  }, [fetchSymptoms]);

  // Set page title, header action and back button
  useEffect(() => {
    if (group) {
      setPageTitle(`Symptomer - ${group.name_no}`);
    } else {
      setPageTitle('Symptomer');
    }

    // Back button
    setBackTo('/admin/symptom-groups');

    if (canEdit) {
      setHeaderAction(
        <button
          onClick={() => {
            setEditingSymptom(null);
            setFormData({
              name_no: '',
              help_text: '',
              risk_tag: '',
              status: 'draft',
            });
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
        >
          <Plus className="h-5 w-5" />
          Nytt symptom
        </button>
      );
    }

    return () => {
      setPageTitle(null);
      setHeaderAction(null);
      setBackTo(null);
    };
  }, [setHeaderAction, setPageTitle, setBackTo, canEdit, group, navigate, symptoms?.length]);

  // Filter symptoms by status and sort based on mode
  const filteredSymptoms = (symptoms?.filter(symptom => {
    if (filterStatus === 'all') return true;
    return symptom.status === filterStatus;
  }) || []).sort((a, b) => {
    if (sortMode === 'alphabetical') {
      return a.name_no.localeCompare(b.name_no, 'no');
    } else {
      // Manual mode: sort by order_index
      return (a.order_index ?? 0) - (b.order_index ?? 0);
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!groupId) return;

    try {
      if (editingSymptom) {
        const { error } = await supabase
          .from('symptoms')
          .update({
            group_id: groupId,
            name_no: formData.name_no,
            help_text: formData.help_text || null,
            risk_tag: formData.risk_tag || null,
          })
          .eq('id', editingSymptom.id);

        if (error) throw error;
      } else {
        // New symptoms are placed at the end of the list
        const maxOrderIndex = symptoms?.reduce((max, s) => Math.max(max, s.order_index || 0), -1) ?? -1;

        const { error } = await supabase
          .from('symptoms')
          .insert({
            group_id: groupId,
            name_no: formData.name_no,
            help_text: formData.help_text || null,
            risk_tag: formData.risk_tag || null,
            order_index: maxOrderIndex + 1,
            status: formData.status,
          });

        if (error) throw error;
      }

      setShowModal(false);
      setEditingSymptom(null);
      resetForm();
      fetchSymptoms();
    } catch (error: any) {
      console.error('Error saving symptom:', error);
      alert(`Feil: ${error.message}`);
    }
  };

  const handleStatusChange = async (id: string, newStatus: ContentStatus) => {
    try {
      const { error } = await supabase
        .from('symptoms')
        .update({ status: newStatus })
        .eq('id', id);

      if (error) throw error;
      fetchSymptoms();
    } catch (error: any) {
      console.error('Error updating status:', error);
      alert(`Feil: ${error.message}`);
    }
  };

  const handleEdit = (symptom: Symptom) => {
    setEditingSymptom(symptom);
    setFormData({
      name_no: symptom.name_no,
      help_text: symptom.help_text || '',
      risk_tag: symptom.risk_tag || '',
      status: symptom.status === 'pending' ? 'draft' : symptom.status,
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Er du sikker på at du vil slette dette symptomet? Dette vil også slette alle koblinger til tilnærminger.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('symptoms')
        .delete()
        .eq('id', id);

      if (error) throw error;
      fetchSymptoms();
    } catch (error: any) {
      console.error('Error deleting symptom:', error);
      alert(`Feil: ${error.message}`);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, symptomId: string) => {
    setDraggedItem(symptomId);
    e.dataTransfer.effectAllowed = 'move';
    setTimeout(() => {
      (e.target as HTMLElement).classList.add('opacity-50');
    }, 0);
  };

  const handleDragEnd = (e: React.DragEvent) => {
    setDraggedItem(null);
    (e.target as HTMLElement).classList.remove('opacity-50');
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, targetSymptomId: string) => {
    e.preventDefault();

    if (!draggedItem || draggedItem === targetSymptomId) {
      setDraggedItem(null);
      return;
    }

    const draggedIndex = filteredSymptoms.findIndex(s => s.id === draggedItem);
    const targetIndex = filteredSymptoms.findIndex(s => s.id === targetSymptomId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    try {
      // Update the dragged item to the target position
      await supabase
        .from('symptoms')
        .update({ order_index: targetIndex })
        .eq('id', draggedItem);

      // Update all items between the old and new positions
      const start = Math.min(draggedIndex, targetIndex);
      const end = Math.max(draggedIndex, targetIndex);

      for (let i = start; i <= end; i++) {
        const symptom = filteredSymptoms[i];
        if (symptom.id !== draggedItem) {
          let newIndex = i;
          if (draggedIndex < targetIndex) {
            newIndex = i - 1;
          } else {
            newIndex = i + 1;
          }
          await supabase
            .from('symptoms')
            .update({ order_index: newIndex })
            .eq('id', symptom.id);
        }
      }

      fetchSymptoms();
    } catch (error: any) {
      console.error('Error reordering:', error);
      alert(`Feil ved omsortering: ${error.message}`);
    }

    setDraggedItem(null);
  };

  const resetForm = () => {
    setFormData({
      name_no: '',
      help_text: '',
      risk_tag: '',
      status: 'draft',
    });
  };

  if (symptoms === undefined || group === undefined) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600 dark:text-gray-400">Laster symptomer...</div>
      </div>
    );
  }

  if (group === null) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <Brain className="h-12 w-12 text-gray-400 mb-4" />
        <p className="text-gray-600 dark:text-gray-400">Symptomgruppen ble ikke funnet</p>
        <button
          onClick={() => navigate('/admin/symptom-groups')}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Tilbake til symptomgrupper
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters and Sort */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Status:</span>
            <div className="flex gap-2">
              {(['all', 'draft', 'published'] as const).map((status) => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  className={`px-3 py-1 rounded-md text-sm font-medium ${
                    filterStatus === status
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {status === 'all' ? 'Alle' : status === 'draft' ? 'Utkast' : 'Publisert'}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Sortering:</span>
            <div className="flex gap-2">
              <button
                onClick={() => setSortMode('alphabetical')}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-sm font-medium ${
                  sortMode === 'alphabetical'
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <ArrowDownAZ className="h-4 w-4" />
                Alfabetisk
              </button>
              <button
                onClick={() => setSortMode('manual')}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-sm font-medium ${
                  sortMode === 'manual'
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <ListOrdered className="h-4 w-4" />
                Manuell
              </button>
            </div>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {filteredSymptoms.length} symptomer
            </span>
          </div>
        </div>

        {sortMode === 'manual' && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
              <GripVertical className="h-4 w-4" />
              Dra og slipp rader for å endre rekkefølgen
            </p>
          </div>
        )}
      </div>

      {/* Symptoms list */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {filteredSymptoms.length === 0 ? (
          <div className="text-center py-12">
            <Brain className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">Ingen symptomer funnet</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              {canEdit ? 'Klikk "Nytt symptom" for å legge til det første symptomet' : ''}
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <tr>
                {sortMode === 'manual' && (
                  <th className="w-12 px-2 py-3"></th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Symptom
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Egenskaper
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Handlinger
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredSymptoms.map((symptom) => (
                <tr
                  key={symptom.id}
                  className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                    sortMode === 'manual' ? 'cursor-grab active:cursor-grabbing' : ''
                  } ${draggedItem === symptom.id ? 'opacity-50 bg-blue-50 dark:bg-blue-900/20' : ''}`}
                  draggable={sortMode === 'manual'}
                  onDragStart={(e) => sortMode === 'manual' && handleDragStart(e, symptom.id)}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver}
                  onDrop={(e) => sortMode === 'manual' && handleDrop(e, symptom.id)}
                >
                  {sortMode === 'manual' && (
                    <td className="w-12 px-2 py-4 text-center">
                      <GripVertical className="h-5 w-5 text-gray-400 dark:text-gray-500 mx-auto" />
                    </td>
                  )}
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">{symptom.name_no}</div>
                      {symptom.help_text && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 flex items-start gap-1">
                          <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
                          {symptom.help_text}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      {symptom.risk_tag ? (
                        <RiskBadge riskTag={symptom.risk_tag} />
                      ) : (
                        <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {symptom.status === 'published' ? (
                      <span
                        onClick={() => canEdit && handleStatusChange(symptom.id, 'draft')}
                        className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-md ${canEdit ? 'cursor-pointer hover:opacity-80' : ''}`}
                        title={canEdit ? 'Klikk for å avpublisere' : undefined}
                      >
                        <CheckCircle className="h-3 w-3" />
                        Publisert
                      </span>
                    ) : (
                      <span
                        onClick={() => canEdit && handleStatusChange(symptom.id, 'published')}
                        className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-orange-100 text-orange-700 rounded-md ${canEdit ? 'cursor-pointer hover:opacity-80' : ''}`}
                        title={canEdit ? 'Klikk for å publisere' : undefined}
                      >
                        <Clock className="h-3 w-3" />
                        Utkast
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-2">
                      {canEdit && (
                        <>
                          <button
                            onClick={() => handleEdit(symptom)}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-500 dark:hover:text-blue-400"
                            title="Rediger"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(symptom.id)}
                            className="text-red-600 hover:text-red-900 dark:text-red-500 dark:hover:text-red-400"
                            title="Slett"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingSymptom(null);
          resetForm();
        }}
        title={editingSymptom ? 'Rediger symptom' : 'Legg til nytt symptom'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name_no" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Navn *
            </label>
            <input
              id="name_no"
              type="text"
              required
              value={formData.name_no}
              onChange={(e) => setFormData({ ...formData, name_no: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="F.eks. Søvnproblemer"
            />
          </div>

          <div>
            <label htmlFor="help_text" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Hjelpetekst
            </label>
            <textarea
              id="help_text"
              rows={2}
              value={formData.help_text}
              onChange={(e) => setFormData({ ...formData, help_text: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Kort forklaring av symptomet..."
            />
          </div>

          <div>
            <label htmlFor="risk_tag" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Risiko-tag
            </label>
            <select
              id="risk_tag"
              value={formData.risk_tag}
              onChange={(e) => setFormData({ ...formData, risk_tag: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {riskOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Status *
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="status"
                  value="draft"
                  checked={formData.status === 'draft'}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as ContentStatus })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <span className="text-gray-700 dark:text-gray-300">Utkast</span>
                <span className="text-xs text-gray-500">(kun synlig for admin)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="status"
                  value="published"
                  checked={formData.status === 'published'}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as ContentStatus })}
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300"
                />
                <span className="text-gray-700 dark:text-gray-300">Publisert</span>
                <span className="text-xs text-gray-500">(synlig for alle)</span>
              </label>
            </div>
          </div>

          {formData.risk_tag && (
            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-md">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-800 dark:text-yellow-200">
                  <p className="font-medium">Viktig om risiko-tags:</p>
                  <p className="mt-1">
                    Symptomer med risiko-tag vil utløse varsler i vurderingen når de velges med høy intensitet.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setShowModal(false);
                setEditingSymptom(null);
                resetForm();
              }}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Avbryt
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
            >
              <Check className="h-4 w-4" />
              {editingSymptom ? 'Oppdater' : 'Opprett'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
